import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  FormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { constant } from '@login/resources/constant';
import { FullRecoveryService } from '@login/services/full-recovery.service';
import { AlertService } from '@shared/services/alert.service';
import { HarpValidators } from '@shared/utils/validators';
import { InputConfig } from '@shared/models/input-config.model';
import { SpinnerService } from '@shared/services/spinner.service';

const CHALLENGE_QUESTIONS = [
  'What is the food you least liked as a child?',
  'What is the name of your first stuffed animal?',
  'What did you earn your first medal or award for?',
  'What is your favorite security question?',
  'What is toy/stuffed animal you liked most as a kid?',
  'What was the first computer game you played?',
  'What is your favorite movie quote?',
  'What was the mascot of the first sports team you played on?',
  'What music album or song did you first purchase?',
  'What was your grandmothers favorite dessert?',
  'What was the first thing you learned to cook?',
  'What was your dream job as a child?',
  'Where did you meet your spouse/significant other?',
  'Where did you go for your favorite vacation?',
  'Where were you on New Years Eve in the year 2000?',
  'Who is your favorite speaker/orator?',
  'Who is your favorite book/movie character?',
  'Who is your favorite sports player?',
];

const CHALLENGE_ANSWER_PATTERN = '^(?! )[a-zA-Z0-9!@#$%^&*|?:;\\s]+[^ +<>()]$';
const PATTERN_ERR_MSG = 'Answer is invalid';
const MIN_LENGTH_ERR_MSG = 'Answer should be at least 4 characters';
const MAX_LENGTH_ERR_MSG = 'Answer should be within 100 characters';

@Component({
  selector: 'app-reset-challenge-question',
  templateUrl: './reset-challenge-question.component.html',
  styleUrls: ['./reset-challenge-question.component.scss'],
})
export class ResetChallengeQuestionComponent implements OnInit {
  isChallengeQuestionReset = false;
  tokenVerificationComplete = false;
  recoveryToken: string;
  validRecoveryToken = false;
  setRecoveryForm: UntypedFormGroup;
  username: string;
  stateToken: string;

  resetChallengeQuestionForm: UntypedFormGroup;
  challengeQuestionInputConfig: InputConfig;
  challengeQuestionAnswerInputConfig: InputConfig;

  constructor(
    private fullRecoveryService: FullRecoveryService,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
  ) {}

  ngOnInit() {
    this.alertService.clearAlert();
    this.recoveryToken = this.route.snapshot.params['recoveryToken'];
    this.verifyToken();
    this.initForm();
  }

  private verifyToken() {
    this.spinnerService.start('Loading...');

    this.fullRecoveryService.verifyToken(this.recoveryToken).subscribe(
      (response: any) => {
        this.tokenVerificationComplete = true;
        this.validRecoveryToken = true;
        this.username = response.username;
        this.stateToken = response.stateToken;
        this.spinnerService.stop();
      },
      (err: any) => {
        this.tokenVerificationComplete = true;
        let alertMessage;
        if (err.error.errorCode) {
          alertMessage = err.error.statusText;
        } else {
          alertMessage = constant.NG_ONINIT_CALL_FAILURE;
        }

        this.alertService.setAlert(alertMessage);
        this.spinnerService.stop();
      },
    );
  }

  initForm() {
    this.resetChallengeQuestionForm = this.fb.group({
      question: ['', [HarpValidators.required()]],
      answer: [
        '',
        [
          HarpValidators.required(),
          HarpValidators.pattern(CHALLENGE_ANSWER_PATTERN, PATTERN_ERR_MSG),
          HarpValidators.minLength(4, MIN_LENGTH_ERR_MSG),
          HarpValidators.maxLength(100, MAX_LENGTH_ERR_MSG),
        ],
      ],
    });

    this.challengeQuestionInputConfig = {
      form: this.resetChallengeQuestionForm,
      name: 'question',
      type: 'select',
      options: CHALLENGE_QUESTIONS.map(question => ({
        label: question,
        value: question,
      })),
      label: 'Challenge Question',
      required: true,
    };

    this.challengeQuestionAnswerInputConfig = {
      form: this.resetChallengeQuestionForm,
      name: 'answer',
      type: 'text',
      label: 'Challenge Question Answer',
      required: true,
    };
  }
  onSetRecovery() {
    this.spinnerService.start('Loading...');
    this.fullRecoveryService
      .restoreAccount(
        this.username,
        this.question,
        this.answer,
        this.stateToken,
      )
      .subscribe(
        (response: any) => {
          this.isChallengeQuestionReset = true;
          this.spinnerService.stop();
        },
        (err: any) => {
          let alertMessage;
          if (err.error.errorCode) {
            alertMessage = err.error.statusText;
          } else {
            alertMessage = constant.ON_SET_RECOV_CALL_FAILURE;
          }
          this.alertService.setAlert(alertMessage);
          this.spinnerService.stop();
        },
      );
  }

  get question() {
    return this.resetChallengeQuestionForm.value.question;
  }

  get answer() {
    return this.resetChallengeQuestionForm.value.answer;
  }
}
